import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { ButtonOpenMenu } from "./ButtonOpenMenu";

const ContentIncludeSection = ({
  config,
  handleInputChange,
  className,
  disabled,
  isOpen,
  setIsOpen,
}) => {
  const { t } = useTranslation();

  return (
    <div className={clsx("param-group", className, { "pb-0": !isOpen })}>
      <div className="param-group-header">
        <h3 className="param-group-title">
          <span>📚</span>
          {t("sections.content.title")}
        </h3>
        {/* Résumé visuel */}
        <div
          className={clsx(
            "p-3 text-center rounded-lg border border-accent-300 bg-accent-50",
            {
              "cursor-pointer bg-accent-100": !isOpen,
            }
          )}
          onClick={() => setIsOpen(true)}
        >
          <div className="mb-2 font-medium text-md text-text-700">
            📊 {t("sections.content.selected")} :
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <span
              className={clsx(
                "px-2 py-1 rounded text-sm",
                config.includeProcedures !== false
                  ? "font-medium bg-primary-100 text-primary-700"
                  : "opacity-50 bg-background-200 text-text-500"
              )}
            >
              {config.includeProcedures !== false ? "✅" : "❌"}{" "}
              {t("sections.content.procedures")}
            </span>
            <span
              className={clsx(
                "px-2 py-1 rounded text-sm",
                config.includeExamples !== false
                  ? "font-medium bg-secondary-100 text-secondary-700"
                  : "opacity-50 bg-background-200 text-text-500"
              )}
            >
              {config.includeExamples !== false ? "✅" : "❌"}{" "}
              {t("sections.content.examples")}
            </span>
            <span
              className={clsx(
                "px-2 py-1 rounded text-sm",
                config.includeIndex === true
                  ? "font-medium bg-accent-100 text-accent-700"
                  : "opacity-50 bg-background-200 text-text-500"
              )}
            >
              {config.includeIndex === true ? "✅" : "❌"}{" "}
              {t("sections.content.index")}
            </span>
          </div>
        </div>
        <ButtonOpenMenu isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>

      <div className={clsx("space-y-4", { hidden: !isOpen })}>
        {/* Procédures recommandées */}
        <div
          className={clsx(
            "p-3 rounded-lg transition-all border",
            config.includeProcedures !== false
              ? "bg-primary-50 border-primary-300"
              : "bg-background-100 border-primary-200"
          )}
        >
          <label className="flex gap-3 items-start cursor-pointer">
            <input
              type="checkbox"
              checked={config.includeProcedures !== false}
              onChange={(e) =>
                handleInputChange("includeProcedures", e.target.checked)
              }
              disabled={disabled}
              className={clsx(
                "mt-1 accent-accent-500",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-text-800">
                📋 {t("sections.content.includeProcedures")}
              </span>
              <p className="mt-1 text-xs text-text-600">
                {t("sections.content.includeProceduresDescription")}
              </p>
            </div>
          </label>
        </div>

        <div className="mb-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-text-800">
            🔧 {t("sections.content.advanced")}
          </h3>
        </div>

        {/* Section principale pour exemples et index */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Exemples */}
          <div
            className={clsx(
              "p-3 rounded-lg transition-all border",
              config.includeExamples !== false
                ? "bg-secondary-50 border-secondary-300"
                : "bg-background-100 border-secondary-200"
            )}
          >
            <label className="flex gap-3 items-start cursor-pointer">
              <input
                type="checkbox"
                checked={config.includeExamples === true}
                onChange={(e) =>
                  handleInputChange("includeExamples", e.target.checked)
                }
                disabled={disabled}
                className={clsx(
                  "mt-1 accent-secondary-500",
                  disabled && "opacity-50 cursor-not-allowed"
                )}
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-text-800">
                  💡 {t("sections.content.includeExamples")}
                </span>
                <p className="mt-1 text-xs text-text-600">
                  {t("sections.content.includeExamplesDescription")}
                </p>
                {config.includeExamples !== false && (
                  <div className="mt-2 text-xs text-secondary-700">
                    ✅ Configuration détaillée disponible ci-dessous
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Index */}
          <div
            className={clsx(
              "p-3 rounded-lg transition-all border",
              config.includeIndex === true
                ? "bg-accent-50 border-accent-300"
                : "bg-background-100 border-accent-200"
            )}
          >
            <label className="flex gap-3 items-start cursor-pointer">
              <input
                type="checkbox"
                checked={config.includeIndex === true}
                onChange={(e) =>
                  handleInputChange("includeIndex", e.target.checked)
                }
                disabled={disabled}
                className={clsx(
                  "mt-1 accent-accent-500",
                  disabled && "opacity-50 cursor-not-allowed"
                )}
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-text-800">
                  📑 {t("sections.content.includeIndex")}
                </span>
                <p className="mt-1 text-xs text-text-600">
                  {t("sections.content.indexDescription")}
                </p>
                {config.includeIndex === true && (
                  <div className="mt-2 text-xs text-accent-700">
                    ✅ {t("sections.content.indexDetailed")}
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentIncludeSection;
